'use client';

import { createContext, useContext, useState, useEffect, ReactNode, Suspense } from 'react';
import { User } from '../types';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { defaultLoadingBox } from '@/app/dashboard/layout';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (redirectTo?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 라우팅 훅을 사용하는 내부 컴포넌트 분리
function AuthProviderInner({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // URL 파라미터 처리 함수
  const handleAuthParams = async () => {
    const code = searchParams?.get('code');
    const data = searchParams?.get('data');
    if (code) {
      // Discord OAuth 코드 처리
      try {

        console.log('이전 페이지 URL:', window.location.href);
        // https://next.kamibot.app/dashboard?code=5OBpgIKPVSoO4Z63ywPyFjwvcl9EqS
        // 여기서  https://next.kamibot.app/dashboard 만 추출
        const redirectUri = window.location.href.split('?')[0];

        const response = await fetch('/api/auth/discord', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            redirectUri: redirectUri
          }),
        });

        if (response.ok) {
          // 파라미터 제거하고 같은 페이지로 리디렉션
          router.replace(pathname ?? '/');
          return true;
        }
      } catch (error) {
        console.error('Discord auth error:', error);
      }
    } else if (data) {
      // Kamibot 인증 데이터 처리
      try {
        const response = await fetch('/api/auth/kamibot', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: data }),
        });
        console.log('Kamibot auth response:', response);
        if (response.ok) {
          // 파라미터 제거하고 같은 페이지로 리디렉션
          router.replace(pathname ?? '/');
          return true;
        }
      } catch (error) {
        console.error('Kamibot auth error:', error);
      }
    }

    return false;
  };

  useEffect(() => {
    // 페이지 로드 시 인증 파라미터 확인 및 사용자 정보 로드
    const initAuth = async () => {
      try {
        // 인증 파라미터 체크 및 처리
        const authHandled = await handleAuthParams();

        // 파라미터 처리 후 또는 파라미터가 없을 때 사용자 정보 로드
        const response = await fetch('/api/users/@me', {
          credentials: 'include', // 쿠키 포함
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData.data);
        } else {
          // 대시보드 페이지에 있는데 로그인이 안된 경우 바로 Discord OAuth로 리디렉션
          if (pathname?.startsWith('/dashboard') && !authHandled) {
            login(pathname);
            return;
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [pathname, searchParams]); // URL이나 경로 변경 시 재실행

  const login = (redirectTo?: string) => {
    // Discord OAuth 설정 - 환경 변수 또는 상수로 관리하는 것이 좋습니다
    const clientId = process.env.NEXT_PUBLIC_DISCORD_CLIENT_ID!;
    let redirectUri = encodeURIComponent(`${window.location.origin}/dashboard`); // 자신의 도메인으로 리다이렉션
    if (redirectUri === 'https%3A%2F%2Flocalhost%2Fdashboard')
      redirectUri = 'https%3A%2F%2Fkamibot.app%2Fdashboard'
    const scope = 'identify email guilds';

    // Discord OAuth 페이지로 직접 리디렉션
    const discordUrl = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&prompt=none&scope=${encodeURIComponent(scope)}`;

    // 현재 URL을 기억하기 위해 state 파라미터 활용도 가능
    window.location.href = discordUrl;
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// 메인 AuthProvider에서는 Suspense 경계 제공
export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={defaultLoadingBox}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </Suspense>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
